// src/hooks/useContentGate.js
//
// Free-tier gating for the ~120 subject pages.
//
// This replaces the earlier slice-based version. Slicing hid the locked
// topics entirely, so a free user saw five cards and no evidence the rest
// existed — nothing to upgrade toward. Now every topic renders, and the
// locked ones intercept clicks instead of disappearing.
//
// Locked cards are NOT greyed out. They look like any other card apart from
// a small badge in the corner, which is the one concession: with no marker
// at all, a card that visibly refuses to open reads as a bug rather than a
// paywall, and people file support tickets instead of upgrading.
//
// ---------------------------------------------------------------------------
// HOW IT HOOKS IN
//
// Rather than rewrite 120 topic-card render blocks, the click is caught once
// on the container in the capture phase, before it can reach any of the
// card's own handlers. Each page needs exactly two attribute insertions,
// both on opening tags, which is what makes the codemod safe:
//
//   <div style={styles.topicsContainer} {...lockProps}>
//   ...
//     <div key={index} {...topicProps(index)} style={{...styles.topicCard}}>
//
// Everything inside the card is untouched.
// ---------------------------------------------------------------------------
//
// A NOTE ON WHAT THIS IS NOT: the topic data is a static import, so every
// video, music, and game URL in the grade ships in the JS bundle whether it
// renders or not. This gate is a UI lock, not an access control — anyone
// willing to open devtools can read the URLs. Fine for K-5 curriculum;
// don't build anything load-bearing on the assumption it's airtight.

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAccessLevel from "./useAccessLevel";

const STYLE_ID = "sbl-topic-lock-styles";

// Injected once per session rather than placed in each page, so the codemod
// doesn't have to find a home for a <style> tag in 120 files.
const LOCK_CSS = `
[data-topic-locked="true"] {
  position: relative;
}
[data-topic-locked="true"]::after {
  content: "🔒 Paid plan";
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(90deg, #e100ff, #7f00ff);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 3px 9px;
  border-radius: 999px;
  pointer-events: none;
  z-index: 3;
  box-shadow: 0 2px 6px rgba(127, 0, 255, 0.35);
}
`;

function useLockStyles(active) {
  useEffect(() => {
    if (!active) return;
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = LOCK_CSS;
    document.head.appendChild(el);
  }, [active]);
}

/**
 * @param {Array} allTopics the page's imported topic list
 * @returns {{
 *   topics: Array,           the full list — nothing is removed
 *   isLocked: (i:number) => boolean,
 *   lockedCount: number,
 *   freeLimit: number,
 *   lockProps: object,       spread onto the topics container
 *   topicProps: (i:number) => object,  spread onto each topic card
 * }}
 */
export default function useContentGate(allTopics) {
  const navigate = useNavigate();
  const { contentLimit, loading, isFree } = useAccessLevel();

  const gateActive = !loading && contentLimit !== Infinity;
  useLockStyles(gateActive);

  const isLocked = useCallback(
    (index) => {
      // While access is still resolving, lock nothing — a brief moment of
      // open cards is better than flashing padlocks at a paying subscriber.
      if (loading) return false;
      if (contentLimit === Infinity) return false;
      return index >= contentLimit;
    },
    [loading, contentLimit]
  );

  const lockedCount = useMemo(() => {
    if (!Array.isArray(allTopics) || !gateActive) return 0;
    return Math.max(0, allTopics.length - contentLimit);
  }, [allTopics, contentLimit, gateActive]);

  // Capture phase: this runs before the card's own onClick, so a locked
  // topic never starts a video or opens a game.
  const onClickCapture = useCallback(
    (event) => {
      if (!gateActive) return;
      const card = event.target?.closest?.("[data-topic-index]");
      if (!card) return;
      const index = Number.parseInt(card.dataset.topicIndex, 10);
      if (Number.isNaN(index) || !isLocked(index)) return;

      event.preventDefault();
      event.stopPropagation();
      navigate("/choose-plan");
    },
    [gateActive, isLocked, navigate]
  );

  const lockProps = useMemo(
    () => (gateActive ? { onClickCapture } : {}),
    [gateActive, onClickCapture]
  );

  const topicProps = useCallback(
    (index) => ({
      "data-topic-index": index,
      "data-topic-locked": isLocked(index) ? "true" : undefined,
    }),
    [isLocked]
  );

  return {
    topics: allTopics,
    isLocked,
    lockedCount,
    freeLimit: contentLimit,
    isFree,
    lockProps,
    topicProps,
  };
}

export { useContentGate };
